/**
 * 소셜 영상 «지금 유효한 재생 주소» — 수집 백엔드 `POST /api/video-urls` 의 계약.
 *
 * 백엔드가 게시물 단위로 주소를 캐시하고 만료되면 다시 받는다. 앱은 묻고, 재생에 실패하면 신고만 한다.
 * 계약서 정본은 수집 백엔드(ops-auto-backend) 레포의 VIDEO-URL-API 문서다.
 *
 * ⚠️ 소셜은 인스타그램·틱톡·유튜브의 2분 미만 영상만 — 그 밖은 `unsupported`.
 * ⚠️ Meta 광고(`meta`)는 수집 때 받은 영상 주소가 만료된 것만 묻는다(id = adArchiveId · 길이 규칙 없음).
 */

/** 묻는 채널 — 이 밖의 채널은 앱이 묻지 않는다 */
export const VIDEO_URL_SOURCES = ['instagram', 'tiktok', 'youtube', 'meta'] as const;
export type VideoUrlSource = (typeof VIDEO_URL_SOURCES)[number];

/** 한 번에 묻는 최대 건수 — 백엔드 `VIDURL_BATCH_MAX` 와 같다 */
export const VIDEO_URL_BATCH_MAX = 24;

/**
 * 받는 중인 건을 상류가 붙잡고 기다려 주는 최대 시간(ms) — 백엔드 `waitMs` 상한과 같다.
 * ⚠️ 그 안에 끝난 건만 `ok`·`unavailable` 로 오고, 나머지는 여전히 `pending` 이다(부분 응답). `waitMs` 를 모르는 백엔드는 무시하고 바로 답한다.
 */
export const VIDEO_URL_WAIT_MAX_MS = 3000;

/**
 * 컨텐츠 허브(미리보기 · 장면 훑기)가 주소를 묻는 영상 길이 상한(초) — 이 길이 이상은 묻지 않는다(건당 유닛 · 큰 파일).
 * ⚠️ 백엔드 상한(`VIDURL_MAX_SEC`)은 600 이다 — 컨텐츠 분석(`analysis-media`)은 앱에서 거르지 않고 백엔드 상한까지 묻는다.
 */
export const VIDEO_URL_MAX_SEC = 120;

/** `durationSec` — 소셜 게시물 `videosMeta[0].duration`(초). 비어 있으면 싣지 않는다(백엔드가 첫 조회 때 잰다) · 광고는 싣지 않는다 */
export type VideoUrlRequestItem = { id: string; source: VideoUrlSource; link: string; durationSec?: number };

/**
 * `ok` 재생 가능 · `pending` 받는 중(같은 id 로 다시 물으면 채워진다) · `unsupported` 대상 아님(다시 묻지 않는다) ·
 * `unavailable` 주소를 못 얻음 · `stale` 실패 신고로 무효화됨(다시 물으면 재발급)
 */
export type VideoUrlStatus = 'ok' | 'pending' | 'unsupported' | 'unavailable' | 'stale';

export type VideoUrlResult = {
  id: string;
  status: VideoUrlStatus;
  /** `ok` 일 때만 채워진다 */
  videoUrl: string;
  posterUrl: string;
  /** epoch ms · 모르면 `null` */
  expiresAt: number | null;
  /** `channel` · `tooLong`(600초 이상) · `noId` · `noUrl` · `gone`(광고 종료) · `error`(받기 실패 — 10분 뒤 다시) 등 · 없으면 빈 문자열 */
  reason: string;
  /** 영상 파일 크기(바이트) — 새로 받은 건에만 있다 · 모르면 비거나 `null` */
  sizeBytes?: number | null;
};
